import axios, { AxiosResponse } from 'axios';
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import {
  fetchCustomerFailure,
  fetchCustomerSuccess,
  updateCustomerFailure,
  updateCustomerRequest,
  updateCustomerSuccess,
} from './actions';
import { FETCH_CUSTOMER_REQUEST, UPDATE_CUSTOMER_REQUEST } from './actionTypes';
import { getPreferredLanguageSelector } from './selectors';
import { applyPreferredLanguage } from '@shared/i18n/Metronici18n';

const getCustomer = () => axios.get<ICustomer>(`/api/v1/customers/1`);

const updateCustomer = async (id: number, params: Partial<ICustomer>) => {
  const response = await axios.put<ICustomer>(`/api/v1/customers/${id}`, params);
  return response.data;
};

function* fetchCustomerSaga() {
  try {
    const response: AxiosResponse<ICustomer> = yield call(getCustomer);

    yield put(
      fetchCustomerSuccess({
        customer: response.data,
      }),
    );

    // The language the customer chose on the storefront, written by petcon onto
    // the PetOwner and surfaced here by customer-dashboard-api. Authoritative:
    // it is applied on every customer fetch, not just the first.
    //
    // Read through the selector rather than off the response so there is one
    // definition of where this value lives — components can use the same one.
    // Selected AFTER the success action so the store already holds the customer.
    //
    // `applyPreferredLanguage` may reload the page, so nothing may be dispatched
    // after it in this saga.
    const preferredLanguage: string | null = yield select(getPreferredLanguageSelector);
    applyPreferredLanguage(preferredLanguage);
  } catch (e: unknown) {
    yield put(
      fetchCustomerFailure({
        error: e instanceof Error ? e.message : 'Unknown error',
      }),
    );
  }
}

function* updateCustomerSaga(action: ReturnType<typeof updateCustomerRequest>) {
  try {
    const response: AxiosResponse<ICustomer> = yield call(() =>
      updateCustomer(action.payload.id, action.payload.params),
    );

    yield put(
      updateCustomerSuccess({
        customer: response.data,
      }),
    );
  } catch (e: any) {
    yield put(
      updateCustomerFailure({
        error: e.message,
      }),
    );
  }
}

export function* customerSaga() {
  yield all([
    takeLatest(FETCH_CUSTOMER_REQUEST, fetchCustomerSaga),
    takeLatest(UPDATE_CUSTOMER_REQUEST, updateCustomerSaga),
  ]);
}
